// The intake chart — the head-to-head.
//
// Three lines against the licence line, over the last sixty days and the next
// nine:
//
//   measured   ink, heavy   the plant's own daily maximum
//   SeaState   accent       our model: fitted BEFORE the window and run on each
//                           day's observed inputs for the past, our estimate
//                           through the reporting gap, our forecast forward
//   PG&E       grey         their incumbent as Les described it — Open-Meteo at
//                           the buoy plus a seasonal offset — on the same inputs
//   threshold  red rule     labelled at the right edge on a knockout
//   range      amber band   the three-model min–max on the intake scale, the
//                           same numbers as the grid's range rows — our own
//                           forecast sits inside it; the station tracks above
//                           show the models parting, this shows what that
//                           means at the plant.
//
// Cody, 2026-10-04: "the honest version is showing that this ISN'T the simple
// model — they already have the dumb model, X buoy minus X degrees." So the
// dumb model is on the chart, and the reader sees on which days the two part
// and who was right. No fills, no bands, no envelope: one line, one colour.
//
// THE LABEL MATTERS. Past days are scored on the archived ocean analysis, not
// on forecasts issued at the time (no source keeps sea temperature as issued —
// checked 2026-08-22 and again 2026-10-04 with the commercial key). Both
// methods get the same water, so this compares the two MAPPINGS onto the
// intake, not forecast skill. The track's source line says so, and the readout
// title says "on the analysis" / "forecast" per day.
//
// WHY NOT chart/chart.ts: that renderer is the fleet meteogram, bound to the
// hourly cursor and window. This is daily; it follows the shared cursor through
// `cursorIndex` and keeps its hover local, like every fleet track.

/** One day. */
export interface TempPoint {
  date: string; // ISO date
  measured?: number | null; // the plant's own daily max
  /** The latest day's max so far (the file stopped mid-day): drawn open, never
   *  joined to the measured line, labelled with the hour it reached. */
  measuredPartial?: number | null;
  measuredTo?: string | null;
  measuredHours?: number | null;
  hindcast?: number | null; // ours, fitted before the window, on observed inputs
  estimate?: number | null; // ours, in the reporting gap
  forecast?: number | null; // ours, forward of today
  pge?: number | null; // PG&E's offset model, same inputs
  /** The bottom and top of the three-model envelope on the intake scale — the
   *  coloured range band — and how many models it spans that day. Present on
   *  every day the payload has it: the forecast, and the models' recent
   *  analysis days behind it. */
  rangeLo?: number | null;
  rangeHi?: number | null;
  members?: number | null;
}

export interface TempChartConfig {
  points: TempPoint[];
  /** Horizontal rules with labels. Only the action threshold forces the y
   *  extent; the ceiling appears if the data gets near it. */
  thresholds?: { value: number; label: string; kind: "action" | "ceiling" }[];
  /** Index of "today"; a vertical divider between record and forecast. */
  todayIndex?: number | null;
  /** The shared playhead as an index into `points`, or null. The crosshair sits
   *  at the LOCAL hover while the pointer is over the plot and falls back to
   *  this — the fleet's rule: hovering a track is inspection, scrubbing the
   *  ruler is navigation. */
  cursorIndex?: number | null;
  unit?: string; // default "°F"
  /** Called as the pointer crosses days (null on leave), so the caller can
   *  repaint the readout strip for the hovered day. */
  onHover?: (p: TempPoint | null, index: number | null) => void;
  /** Fallback height when the canvas has no laid-out height; `.chart-wrap`
   *  normally sizes it. */
  height?: number;
}

export interface TempChart {
  update(config: Partial<TempChartConfig>): void;
  destroy(): void;
}

function themed(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v === "" ? fallback : v;
}

function niceStep(raw: number): number {
  if (!(raw > 0)) return 1;
  const exp = Math.floor(Math.log10(raw));
  const base = Math.pow(10, exp);
  const f = raw / base;
  const nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nf * base;
}

function ours(p: TempPoint): number | null | undefined {
  return p.hindcast ?? p.estimate ?? p.forecast;
}

/** Every finite number a point contributes to the y extent — only what is drawn. */
function valuesOf(p: TempPoint): number[] {
  const out: number[] = [];
  for (const v of [p.measured, p.measuredPartial, ours(p), p.pge, p.rangeLo, p.rangeHi]) {
    if (typeof v === "number" && Number.isFinite(v)) out.push(v);
  }
  return out;
}

const FONT = "10.5px ui-monospace, SFMono-Regular, Menlo, monospace";

export function createTempChart(
  canvas: HTMLCanvasElement,
  config: TempChartConfig,
): TempChart {
  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new Error("tempchart: 2d context unavailable");
  const g = ctx;

  const cfg: TempChartConfig = {
    points: config.points,
    thresholds: config.thresholds ?? [],
    todayIndex: config.todayIndex ?? null,
    cursorIndex: config.cursorIndex ?? null,
    unit: config.unit ?? "°F",
    onHover: config.onHover,
    height: config.height ?? 240,
  };

  let raf = false;
  let lastHover: number | null = null;

  // 46 left matches the stack's shared gutter (tracks.ts GUTTER) and 10 right
  // matches chart.ts, so a date sits at the same x here as on the ruler and on
  // every station track.
  const PAD = { left: 46, right: 10, top: 10, bottom: 8 };

  function extent(): { min: number; max: number } {
    let min = Infinity;
    let max = -Infinity;
    for (const p of cfg.points) {
      for (const v of valuesOf(p)) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
    for (const t of cfg.thresholds ?? []) {
      if (t.kind !== "action") continue;
      if (t.value < min) min = t.value;
      if (t.value > max) max = t.value;
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return { min: 0, max: 1 };
    const pad = Math.max(0.5, (max - min) * 0.1);
    return { min: min - pad, max: max + pad };
  }

  function draw(): void {
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const cssW = canvas.clientWidth || 600;
    const cssH = canvas.clientHeight || cfg.height || 240;
    const bw = Math.round(cssW * dpr);
    const bh = Math.round(cssH * dpr);
    if (canvas.width !== bw) canvas.width = bw;
    if (canvas.height !== bh) canvas.height = bh;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, cssW, cssH);

    const n = cfg.points.length;
    if (n === 0) return;

    const line = themed("--line", "#d8d8d4");
    const muted = themed("--muted", "#6b7280");
    const ink = themed("--ink", "#16232e");
    const accent = themed("--accent", "#0f6b74");
    const crit = themed("--crit", "#b23b3b");
    const paper = themed("--paper", "#ffffff");

    const { min, max } = extent();
    const left = PAD.left;
    const right = cssW - PAD.right;
    const top = PAD.top;
    const bottom = cssH - PAD.bottom;
    const xPx = (i: number): number =>
      n === 1 ? (left + right) / 2 : left + (i / (n - 1)) * (right - left);
    const yPx = (v: number): number =>
      bottom - ((v - min) / (max - min)) * (bottom - top);

    // y grid and ticks, with the unit, like every other track. Divided by
    // eight, not five: a 12 °F span on a 280 px plot got two ticks at a 5 °F
    // step, which is not a scale a reader can take a value off.
    const step = niceStep((max - min) / 8);
    g.font = FONT;
    g.textBaseline = "middle";
    for (let v = Math.ceil(min / step) * step; v <= max; v += step) {
      const y = Math.round(yPx(v)) + 0.5;
      g.strokeStyle = line;
      g.lineWidth = 1;
      g.beginPath();
      g.moveTo(left, y);
      g.lineTo(right, y);
      g.stroke();
      g.fillStyle = muted;
      g.textAlign = "right";
      g.fillText(`${Math.round(v)} ${cfg.unit ?? "°F"}`, left - 6, y);
    }

    // today divider
    if (cfg.todayIndex !== null && cfg.todayIndex !== undefined) {
      const x = Math.round(xPx(cfg.todayIndex)) + 0.5;
      g.strokeStyle = muted;
      g.lineWidth = 1;
      g.setLineDash([2, 3]);
      g.beginPath();
      g.moveTo(x, top);
      g.lineTo(x, bottom);
      g.stroke();
      g.setLineDash([]);
    }

    /** One series, gaps skipped rather than bridged; a lone day is a dot. */
    const stroke = (
      pick: (p: TempPoint) => number | null | undefined,
      color: string,
      width: number,
      dash: number[] = [],
    ): void => {
      g.strokeStyle = color;
      g.fillStyle = color;
      g.lineWidth = width;
      g.setLineDash(dash);
      g.lineJoin = "round";
      g.lineCap = "round";
      let run: number[] = [];
      const flush = (): void => {
        if (run.length === 1) {
          const i = run[0]!;
          g.beginPath();
          g.arc(xPx(i), yPx(pick(cfg.points[i]!) as number), width * 0.9 + 1, 0, Math.PI * 2);
          g.fill();
        } else if (run.length > 1) {
          g.beginPath();
          run.forEach((i, k) => {
            const x = xPx(i);
            const y = yPx(pick(cfg.points[i]!) as number);
            if (k === 0) g.moveTo(x, y);
            else g.lineTo(x, y);
          });
          g.stroke();
        }
        run = [];
      };
      g.setLineDash([]);
      for (let i = 0; i < n; i++) {
        const v = pick(cfg.points[i]!);
        if (typeof v !== "number" || !Number.isFinite(v)) {
          flush();
          continue;
        }
        run.push(i);
      }
      flush();
    };

    // THE RANGE first, under everything: the coloured min–max band, amber like
    // the grid's "Range high / Range low" rows, with crisp edges. Cody: "I want
    // the colored range min max, like you had it before, just like the bottom
    // has it." Thin dashed edges alone were invisible at a glance. Drawn on
    // every day the payload has it (the models' analysis days too), so the
    // reader can see whether the water fell inside the models' span.
    const warn = themed("--warn", "#b46a00");
    const rng = cfg.points
      .map((p, i) => ({ i, lo: p.rangeLo, hi: p.rangeHi }))
      .filter((b) => typeof b.lo === "number" && typeof b.hi === "number");
    if (rng.length > 1) {
      g.fillStyle = warn;
      g.globalAlpha = 0.22;
      g.beginPath();
      rng.forEach((b, k) => {
        const x = xPx(b.i);
        const y = yPx(b.hi as number);
        if (k === 0) g.moveTo(x, y);
        else g.lineTo(x, y);
      });
      for (let k = rng.length - 1; k >= 0; k--) {
        const b = rng[k]!;
        g.lineTo(xPx(b.i), yPx(b.lo as number));
      }
      g.closePath();
      g.fill();
      g.globalAlpha = 1;
    }
    stroke((p) => p.rangeHi, warn, 1.2);
    stroke((p) => p.rangeLo, warn, 1.2);
    // The incumbent next, under ours; the measurement on top of both.
    stroke((p) => p.pge, muted, 1.8);
    stroke(ours, accent, 1.8);
    stroke((p) => p.measured, ink, 2.4);
    // The partial day: an open ring in ink, the size of a lone-day dot, not on
    // the line — a day that is still being measured is not a measurement of
    // the day.
    for (let i = 0; i < cfg.points.length; i++) {
      const v = cfg.points[i]?.measuredPartial;
      if (typeof v !== "number" || !Number.isFinite(v)) continue;
      g.beginPath();
      g.lineWidth = 2;
      g.strokeStyle = ink;
      g.fillStyle = paper;
      g.arc(xPx(i), yPx(v), 3.6, 0, Math.PI * 2);
      g.fill();
      g.stroke();
    }

    // thresholds, labelled at the right edge on a knockout
    g.font = FONT;
    for (const t of cfg.thresholds ?? []) {
      if (t.value < min || t.value > max) continue;
      const y = Math.round(yPx(t.value)) + 0.5;
      const isAction = t.kind === "action";
      g.strokeStyle = isAction ? crit : muted;
      g.lineWidth = isAction ? 1.4 : 1;
      g.setLineDash(isAction ? [7, 4] : [2, 3]);
      g.beginPath();
      g.moveTo(left, y);
      g.lineTo(right, y);
      g.stroke();
      g.setLineDash([]);
      // Label above the rule, unless the rule is within a label's height of the
      // top of the plot — the ceiling sits there whenever the range reaches it,
      // and a label drawn above it is cut off by the canvas edge.
      g.textAlign = "right";
      const w = g.measureText(t.label).width;
      const below = y - 16 < top;
      g.textBaseline = below ? "top" : "bottom";
      const ty = below ? y + 3 : y - 2;
      g.fillStyle = paper;
      g.globalAlpha = 0.85;
      g.fillRect(right - w - 8, below ? ty - 1 : ty - 13, w + 8, 14);
      g.globalAlpha = 1;
      g.fillStyle = isAction ? crit : muted;
      g.fillText(t.label, right - 4, ty);
    }

    // the playhead: the local hover if there is one, else the shared cursor
    const ci = lastHover ?? cfg.cursorIndex;
    if (ci !== null && ci !== undefined && ci >= 0 && ci < n) {
      const x = Math.round(xPx(ci)) + 0.5;
      g.strokeStyle = ink;
      g.globalAlpha = 0.4;
      g.lineWidth = 1;
      g.beginPath();
      g.moveTo(x, top);
      g.lineTo(x, bottom);
      g.stroke();
      g.globalAlpha = 1;
      const pt = cfg.points[ci]!;
      const dot = (v: number | null | undefined, color: string): void => {
        if (typeof v !== "number" || !Number.isFinite(v)) return;
        g.fillStyle = color;
        g.beginPath();
        g.arc(x, yPx(v), 3, 0, Math.PI * 2);
        g.fill();
      };
      dot(pt.pge, muted);
      dot(ours(pt), accent);
      dot(pt.measured, ink);
      if (typeof pt.measuredPartial === "number") {
        g.beginPath();
        g.lineWidth = 2;
        g.strokeStyle = ink;
        g.fillStyle = paper;
        g.arc(x, yPx(pt.measuredPartial), 3.6, 0, Math.PI * 2);
        g.fill();
        g.stroke();
      }
    }
  }

  function requestDraw(): void {
    if (raf) return;
    raf = true;
    requestAnimationFrame(() => {
      raf = false;
      draw();
    });
  }

  function indexAt(clientX: number): number | null {
    const rect = canvas.getBoundingClientRect();
    const n = cfg.points.length;
    if (n === 0) return null;
    const left = PAD.left;
    const right = rect.width - PAD.right;
    const t = (clientX - rect.left - left) / Math.max(1, right - left);
    if (t < -0.02 || t > 1.02) return null;
    return Math.max(0, Math.min(n - 1, Math.round(t * (n - 1))));
  }

  const onMove = (e: PointerEvent): void => {
    const i = indexAt(e.clientX);
    if (i === lastHover) return;
    lastHover = i;
    cfg.onHover?.(i === null ? null : (cfg.points[i] ?? null), i);
    requestDraw();
  };
  const onLeave = (): void => {
    if (lastHover === null) return;
    lastHover = null;
    cfg.onHover?.(null, null);
    requestDraw();
  };

  canvas.addEventListener("pointermove", onMove);
  canvas.addEventListener("pointerleave", onLeave);

  const ro = new ResizeObserver(() => requestDraw());
  ro.observe(canvas);

  requestDraw();

  return {
    update(next: Partial<TempChartConfig>): void {
      Object.assign(cfg, next);
      requestDraw();
    },
    destroy(): void {
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerleave", onLeave);
      ro.disconnect();
    },
  };
}
